<template>
  <div ref="widgetRoot" class="reviews-widget-shell">
    <div ref="widgetBox" :class="['reviews-widget', { 'reviews-widget-preview': previewMode }]">
      <div v-if="reviews.length === 0" class="widget-state">
        {{ labels.empty }}
      </div>

      <div
        v-else
        class="carousel-shell"
        @mouseenter="pauseAutoScroll"
        @mouseleave="resumeAutoScroll"
        @focusin="pauseAutoScroll"
        @focusout="resumeAutoScroll"
      >
        <button
          v-if="totalPages > 1"
          class="carousel-button prev"
          @click="scrollReviews(-1)"
          aria-label="Previous reviews"
        >
          <i class="fa fa-angle-left"></i>
        </button>

        <div
          ref="reviewsTrack"
          :class="['reviews-list', { 'reviews-list-centered': fitsInSinglePage }]"
          @scroll="updateCurrentReviewIndex"
        >
          <div
            v-for="(review, index) in reviews"
            :key="reviewKey(review, index)"
            :class="reviewCardClasses(review, index)"
            :style="reviewCardStyle"
          >
            <div class="reviews-card-layout">
              <div class="reviews-card-source-badge">
                <img v-if="sourceImage(review.source)" :src="sourceImage(review.source)" :alt="review.sourceLabel || review.source" />
              </div>

              <div class="reviews-card-meta">
                <div class="reviews-profile">
                  <div class="reviews-profile-content">
                    <div v-if="review.rating !== null" class="reviews-star-rating">
                      <div class="reviews-stars-active" :style="'width:' + normalizedRatingPercentage(review) + '%'">
                        <i v-for="n in 5" :key="'active-' + index + '-' + n" class="fa fa-star"></i>
                      </div>
                      <div class="reviews-stars-inactive">
                        <i v-for="n in 5" :key="'inactive-' + index + '-' + n" class="fa fa-star"></i>
                      </div>
                    </div>
                    <p class="reviews-profile-name">{{ review.reviewerName }}</p>
                    <p class="reviews-profile-subline">{{ formatDate(review.publishedDate) }}</p>
                  </div>
                </div>
              </div>

              <div class="reviews-card-content" :dir="reviewDirection(review)">
                <div :class="['reviews-card-copy', { 'reviews-card-copy-rtl': isRtlReview(review) }]">
                  <p v-if="displayReviewTitle(review)" class="embed-review-title">
                    {{ displayReviewTitle(review) }}
                  </p>
                  <p
                    :ref="`reviewText-${reviewKey(review, index)}`"
                    :class="['embed-review-text', { 'embed-review-text-collapsed': !isReviewExpanded(review, index) }]"
                  >
                    {{ displayReviewText(review.text) }}
                  </p>
                </div>
                <button
                  v-if="doesReviewOverflow(review, index)"
                  type="button"
                  class="review-expand-button"
                  @click="toggleReviewExpanded(review, index)"
                >
                  {{ isReviewExpanded(review, index) ? labels.showLess : labels.readMore }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          v-if="totalPages > 1"
          class="carousel-button next"
          @click="scrollReviews(1)"
          aria-label="Next reviews"
        >
          <i class="fa fa-angle-right"></i>
        </button>

        <p v-if="totalPages > 1" class="carousel-position-indicator">{{ currentPageNumber }} / {{ totalPages }}</p>
      </div>
    </div>
  </div>
</template>

<script>
// Long enough to read a collapsed card (3-4 clamped lines) before the track moves on.
const AUTO_SCROLL_INTERVAL_MS = 6000;

// Narrowest a review card may get before we drop a column. Below roughly this
// width the reviewer name and date wrap onto three lines and the body text is
// clipped by the card's fixed height rather than simply running shorter.
const MIN_CARD_WIDTH = 280;

export default {
  name: "ReviewsWidget",
  props: ["data", "widgetId"],
  data() {
    return {
      resizeObserver: null,
      handleWindowResize: null,
      autoScrollTimer: null,
      autoScrollPaused: false,
      prefersReducedMotion: false,
      currentPageIndex: 0,
      expandedReviews: {},
      overflowingReviews: {},
      previewMode: false,
      reviews: [],
      widgetWidth: window.innerWidth,
      requestedCards: 1,
      locale: "en",
      labels: {
        heading: "What our customers say",
        empty: "No public reviews are available for the selected channels.",
        readMore: "Read more",
        showLess: "Show less",
      },
    };
  },
  computed: {
    cardsPerView() {
      // widgetWidth is the inner .reviews-widget box, i.e. the space the cards
      // are really laid out in. Fixed breakpoints can't work here: the box is
      // now as wide as the host's container, so any threshold either starves a
      // wide embed of columns or crams three ~160px cards into a narrow one.
      // Fitting as many of the requested cards as MIN_CARD_WIDTH allows keeps
      // the embedder's `cards` setting honoured at any container width.
      const fits = Math.floor(this.widgetWidth / MIN_CARD_WIDTH);
      return Math.max(1, Math.min(this.requestedCards, fits));
    },
    currentPageNumber() {
      return this.reviews.length === 0 ? 0 : this.currentPageIndex + 1;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.reviews.length / this.cardsPerView));
    },
    fitsInSinglePage() {
      // Fewer reviews than slots (e.g. limit=2 with cards=3): the track never
      // overflows, so centring is safe - justify-content: center on an
      // overflowing flex row would make its first card unreachable.
      return this.reviews.length <= this.cardsPerView;
    },
    reviewCardStyle() {
      if (this.cardsPerView <= 1) {
        return { width: "100%" };
      }
      return { width: `calc((100% - ${16 * (this.cardsPerView - 1)}px) / ${this.cardsPerView})` };
    },
  },
  methods: {
    getRequestedCards() {
      const params = new URLSearchParams(window.location.search || "");
      const cards = Number(params.get("cards") || 1);
      if (!Number.isFinite(cards) || cards < 1) return 1;
      return Math.min(Math.floor(cards), 3);
    },
    isPreviewMode() {
      const params = new URLSearchParams(window.location.search || "");
      return params.get("preview") === "1";
    },
    getRequestedLocale() {
      const params = new URLSearchParams(window.location.search || "");
      return params.get("lang") || "en";
    },
    notifyParentHeight(height) {
      const urlParams = new URLSearchParams(window.location.search);
      const iframeID = urlParams.get("iframeID");
      if (!iframeID || !this.$refs.widgetRoot) {
        return;
      }

      const measuredHeight = height || Math.ceil(this.$refs.widgetRoot.getBoundingClientRect().height);
      window.top.postMessage(
        { event: "updateHeight", scrollHeight: measuredHeight, id: iframeID },
        "*"
      );
    },
    observeResize() {
      const root = this.$refs.widgetRoot;
      if (!root) return;

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(([entry]) => this.handleResize(entry.contentRect));
        this.resizeObserver.observe(root);
      } else {
        this.handleWindowResize = () => this.handleResize(root.getBoundingClientRect());
        window.addEventListener("resize", this.handleWindowResize);
        this.handleWindowResize();
      }
    },
    handleResize(rect) {
      // The shell (rect) is full-bleed; the box is what constrains the cards.
      // Height still comes from the shell, which is what the parent iframe sizes to.
      const box = this.$refs.widgetBox;
      this.widgetWidth = box ? box.getBoundingClientRect().width : rect.width;
      this.measureReviewOverflow();
      this.updateCurrentReviewIndex();
      this.notifyParentHeight(Math.ceil(rect.height));
    },
    reviewKey(review, index) {
      return review && review.id ? review.id : index;
    },
    reviewDirection(review) {
      return this.isRtlReview(review) ? "rtl" : "ltr";
    },
    isRtlReview(review) {
      const text = `${this.displayReviewTitle(review)} ${this.displayReviewText(review && review.text)}`;
      return /[\u0590-\u05FF\u0600-\u06FF]/.test(text);
    },
    isReviewExpanded(review, index) {
      return Boolean(this.expandedReviews[this.reviewKey(review, index)]);
    },
    doesReviewOverflow(review, index) {
      return Boolean(this.overflowingReviews[this.reviewKey(review, index)]);
    },
    toggleReviewExpanded(review, index) {
      const key = this.reviewKey(review, index);
      const expanded = !this.isReviewExpanded(review, index);
      this.$set(this.expandedReviews, key, expanded);
      // Someone reading an expanded card shouldn't have it scrolled out from under them.
      if (expanded) {
        this.pauseAutoScroll();
      } else if (!Object.keys(this.expandedReviews).some((k) => this.expandedReviews[k])) {
        this.resumeAutoScroll();
      }
      this.$nextTick(() => this.notifyParentHeight());
    },
    reviewCardClasses(review, index) {
      return [
        "reviews-card",
        {
          "reviews-card-expanded": this.isReviewExpanded(review, index),
          "reviews-card-multi": this.cardsPerView > 1,
          "reviews-card-compact": this.cardsPerView > 2,
        },
      ];
    },
    measureReviewOverflow() {
      const overflowing = { ...this.overflowingReviews };

      this.reviews.forEach((review, index) => {
        const key = this.reviewKey(review, index);
        if (this.isReviewExpanded(review, index)) {
          overflowing[key] = true;
          return;
        }

        const ref = this.$refs[`reviewText-${key}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) {
          overflowing[key] = el.scrollHeight > el.clientHeight + 1;
        }
      });

      this.overflowingReviews = overflowing;
    },
    formatDate(date) {
      if (!date) return "";
      const options = { year: "numeric", month: "short", day: "numeric" };
      try {
        return new Intl.DateTimeFormat(this.locale, options).format(new Date(date));
      } catch (e) {
        return new Intl.DateTimeFormat("en", options).format(new Date(date));
      }
    },
    normalizedRatingPercentage(review) {
      if (!review || review.rating === null || !review.ratingOutOf) return 0;
      return (review.rating / review.ratingOutOf) * 100;
    },
    displayReviewText(text) {
      return String(text || "")
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    },
    displayReviewTitle(review) {
      const title = String((review && review.title) || "").trim();
      if (!title) return "";
      return title === this.displayReviewText(review && review.text) ? "" : title;
    },
    scrollReviews(direction) {
      const track = this.$refs.reviewsTrack;
      if (!track) return;
      track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
      // Restart the countdown so a manual click isn't immediately overridden.
      this.startAutoScroll();
    },
    startAutoScroll() {
      this.stopAutoScroll();
      if (this.autoScrollPaused || this.prefersReducedMotion || this.totalPages <= 1) {
        return;
      }
      this.autoScrollTimer = window.setInterval(() => this.advanceAutoScroll(), AUTO_SCROLL_INTERVAL_MS);
    },
    stopAutoScroll() {
      if (this.autoScrollTimer) {
        window.clearInterval(this.autoScrollTimer);
        this.autoScrollTimer = null;
      }
    },
    pauseAutoScroll() {
      this.autoScrollPaused = true;
      this.stopAutoScroll();
    },
    resumeAutoScroll() {
      this.autoScrollPaused = false;
      this.startAutoScroll();
    },
    advanceAutoScroll() {
      const track = this.$refs.reviewsTrack;
      if (!track) return;
      const nextPage = (this.currentPageIndex + 1) % this.totalPages;
      track.scrollTo({ left: nextPage * track.clientWidth, behavior: "smooth" });
    },
    updateCurrentReviewIndex() {
      const track = this.$refs.reviewsTrack;
      if (!track || this.reviews.length === 0) {
        this.currentPageIndex = 0;
        return;
      }
      const nextIndex = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      this.currentPageIndex = Math.max(0, Math.min(this.totalPages - 1, nextIndex));
    },
    sourceImage(source) {
      try {
        return require(`../assets/reviews/${source}.png`);
      } catch (e) {
        return null;
      }
    },
  },
  mounted() {
    this.previewMode = this.isPreviewMode();
    this.requestedCards = this.getRequestedCards();
    this.locale = this.getRequestedLocale();
    if (this.data && this.data.labels) {
      this.labels = { ...this.labels, ...this.data.labels };
    }
    this.reviews = Array.isArray(this.data && this.data.reviews) ? this.data.reviews : [];
    this.prefersReducedMotion = Boolean(
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
    this.observeResize();
    this.$nextTick(() => {
      this.measureReviewOverflow();
      this.notifyParentHeight();
      this.startAutoScroll();
    });
  },
  watch: {
    // A resize can change cardsPerView, and with it how many pages exist -
    // restart so the timer wraps at the new page count (no-ops while paused).
    totalPages() {
      this.startAutoScroll();
    },
  },
  beforeDestroy() {
    this.stopAutoScroll();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.handleWindowResize) {
      window.removeEventListener("resize", this.handleWindowResize);
    }
  },
};
</script>

<style scoped>
/*
  Single defensive box-sizing reset for the whole embed, instead of
  repeating "box-sizing: border-box" on six separate selectors.
  Kept because this is a third-party embed and the host page's CSS
  could otherwise leak in.
*/
.reviews-widget-shell,
.reviews-widget-shell * {
  box-sizing: border-box;
}

.reviews-widget-shell {
  background: transparent;
  padding: 0;
}

.reviews-widget {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  margin: 0 auto;
  max-width: 100%;
  padding: 16px 16px 14px;
}

.reviews-widget-preview {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  max-width: 640px;
  padding: 0;
}

.widget-state {
  color: #5f6f87;
  font-family: "Open Sans", sans-serif;
  font-size: 13px;
  padding: 18px 0 8px;
  text-align: center;
}

.carousel-shell {
  display: block;
  padding: 8px 34px 4px;
  position: relative;
}

.reviews-widget-preview .carousel-shell {
  padding: 0 18px 4px;
}

.reviews-list {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 2px 1px 8px;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  width: 100%;
  /* Scrollbar hidden: the track advances on its own and via the arrow buttons.
     Firefox / legacy Edge / WebKit each need their own property. */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.reviews-list::-webkit-scrollbar {
  display: none;
  height: 0;
  width: 0;
}

.reviews-list-centered {
  justify-content: center;
}

.reviews-card {
  align-self: flex-start;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #d7e4f2;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(30, 49, 74, 0.06);
  flex: 0 0 auto;
  height: 184px;
  min-height: 0;
  overflow: hidden;
  scroll-snap-align: start;
}

.reviews-widget-preview .reviews-card {
  height: 176px;
}

.reviews-card-multi {
  height: 204px;
}

.reviews-card-compact {
  height: 216px;
}

.reviews-card-expanded {
  height: 280px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.reviews-card-layout {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px 10px;
  position: relative;
}

.reviews-widget-preview .reviews-card-layout {
  padding: 12px 14px 10px;
}

.reviews-card-meta,
.reviews-card-content {
  min-width: 0;
  width: 100%;
}

.reviews-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.reviews-card-copy {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.reviews-card-copy-rtl {
  text-align: right;
}

.reviews-card-expanded .reviews-card-content,
.reviews-card-expanded .reviews-card-layout {
  height: auto;
}

.reviews-card-expanded .reviews-card-copy {
  overflow: visible;
}

.reviews-card-source-badge {
  align-items: center;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  position: absolute;
  right: 14px;
  top: 12px;
  width: 54px;
}

.reviews-card-source-badge img {
  max-height: 22px;
  max-width: 54px;
  object-fit: contain;
}

.reviews-card-compact .reviews-card-source-badge {
  right: 10px;
  top: 10px;
  width: 42px;
}

.reviews-card-compact .reviews-card-source-badge img {
  max-height: 18px;
  max-width: 42px;
}

.reviews-profile {
  align-items: flex-start;
  display: block;
  padding-right: 64px;
}

.reviews-card-compact .reviews-profile {
  padding-right: 48px;
}

.reviews-profile-name,
.embed-review-title,
.embed-review-text,
.review-expand-button,
.reviews-profile-subline,
.carousel-position-indicator {
  font-family: "Open Sans", sans-serif;
}

.reviews-profile-name {
  color: #2f7fbe;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 2px;
}

.reviews-card-compact .reviews-profile-name {
  font-size: 13px;
}

.reviews-widget-preview .reviews-profile-name {
  font-size: 13px;
}

.reviews-profile-subline {
  color: #7e88a7;
  font-size: 10px;
  line-height: 1.3;
  margin: 0 0 4px;
}

.reviews-star-rating {
  align-items: center;
  display: inline-flex;
  gap: 4px;
  margin-bottom: 4px;
  position: relative;
}

.reviews-stars-active,
.reviews-stars-inactive {
  font-size: 12px;
  line-height: 1;
}

.reviews-stars-active {
  color: #ffbf1f;
  left: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  white-space: nowrap;
}

.reviews-stars-inactive {
  color: #f3e3aa;
  white-space: nowrap;
}

.embed-review-title {
  color: #2d241c;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 6px;
}

.reviews-widget-preview .embed-review-title {
  font-size: 14px;
}

.embed-review-text {
  color: #4f5d73;
  font-size: 12px;
  line-height: 1.45;
  margin: 0;
  white-space: pre-line;
}

.reviews-widget-preview .embed-review-text {
  line-height: 1.4;
}

.embed-review-text-collapsed {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
}

.reviews-widget-preview .embed-review-text-collapsed {
  -webkit-line-clamp: 3;
}

.review-expand-button {
  align-self: flex-start;
  background: none;
  border: 0;
  color: #2f7fbe;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  margin-top: 6px;
  padding: 0;
}

.carousel-button {
  align-items: center;
  background: #fff;
  border: 1px solid #d9e3ef;
  border-radius: 999px;
  box-shadow: 0 6px 12px rgba(30, 49, 74, 0.08);
  color: #2f7fbe;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  z-index: 2;
}

.reviews-widget-preview .carousel-button {
  height: 28px;
  width: 28px;
}

.carousel-button.prev {
  left: 0;
}

.carousel-button.next {
  right: 0;
}

.carousel-position-indicator {
  color: #7e88a7;
  font-size: 11px;
  margin: 6px 0 0;
  text-align: center;
}

.reviews-widget-preview .carousel-position-indicator {
  margin-top: 6px;
}

/*
  Arrows are hidden for touch, not for narrow. The media query below used to be
  a width one (max-width: 767px), but it measures the IFRAME's viewport, which
  equals the host container - so a 640px sidebar on a desktop site lost its
  arrows and left mouse users with no way to page the track. Keyed on hover
  instead: touch devices swipe and get the full-bleed track, anything with a
  pointer keeps the arrows at every width.
*/
@media (hover: none) {
  .carousel-button {
    display: none;
  }

  .carousel-shell {
    padding-left: 0;
    padding-right: 0;
  }
}

@media (max-width: 767px) {
  .reviews-widget {
    border-radius: 12px;
    max-width: 100%;
    padding: 12px 10px 10px;
  }

  .carousel-shell {
    padding: 6px 30px 4px;
  }

  .reviews-list {
    gap: 8px;
    padding: 2px 1px 6px;
  }

  .reviews-card {
    border-radius: 10px;
    height: auto;
    min-height: 172px;
  }

  .reviews-card-expanded {
    min-height: 228px;
  }

  .reviews-card-layout {
    gap: 8px;
    padding: 10px 12px 8px;
  }

  .reviews-profile {
    padding-right: 56px;
  }

  .reviews-card-source-badge {
    right: 12px;
    top: 10px;
    width: 48px;
  }

  .reviews-card-source-badge img {
    max-height: 20px;
    max-width: 48px;
  }

  .reviews-profile-name {
    font-size: 13px;
  }

  .embed-review-title {
    font-size: 12px;
    margin-bottom: 4px;
  }

  .embed-review-text {
    font-size: 11px;
    line-height: 1.4;
  }

  .review-expand-button {
    font-size: 10px;
    margin-top: 4px;
  }

  .carousel-position-indicator {
    font-size: 10px;
    margin-top: 4px;
  }
}

@media (max-width: 479px) {
  .reviews-widget {
    padding: 10px 8px 8px;
  }

  .reviews-card {
    min-height: 160px;
  }

  .reviews-card-layout {
    padding: 9px 10px 8px;
  }

  .reviews-profile {
    padding-right: 52px;
  }

  .reviews-stars-active,
  .reviews-stars-inactive {
    font-size: 11px;
  }

  .embed-review-text-collapsed {
    -webkit-line-clamp: 5;
  }
}
</style>
